import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Download as DownloadIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  GppBad as GppBadIcon,
  GppMaybe as GppMaybeIcon
} from '@mui/icons-material';
import { unlockControls, approveAssessment, getEvidenceDownloadUrl } from 'services/vendor-assessment-service';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

/**
 * GapAnalysisView Component
 * Internal reviewer interface for gap analysis and control unlock
 */
export const GapAnalysisView = ({ assessment, controls, trustScore, onRefresh }) => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);

  const [selectedControls, setSelectedControls] = useState([]);
  const [notes, setNotes] = useState('');

  // Unlock mutation
  const unlockMutation = useMutation(
    ({ controlIds, unlockNotes }) => unlockControls(assessment._id, controlIds, unlockNotes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', assessment._id]);
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.gapView.swal.unlockSuccessTitle'),
          text: t('gap-analysis:assessment.gapView.swal.unlockSuccessText', { count: selectedControls.length }),
          timer: 2000,
          customClass: { container: 'swal-high-z-index' }
        });
        setSelectedControls([]);
        setNotes('');
        if (onRefresh) onRefresh();
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.gapView.swal.errorTitle'),
          text: error.response?.data?.message || t('gap-analysis:assessment.gapView.swal.errorUnlock'),
          customClass: { container: 'swal-high-z-index' }
        });
      }
    }
  );

  // Approve mutation
  const approveMutation = useMutation(
    () => approveAssessment(assessment._id, notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', assessment._id]);
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.gapView.swal.approveSuccessTitle'),
          text: t('gap-analysis:assessment.gapView.swal.approveSuccessText'),
          timer: 2000,
          customClass: { container: 'swal-high-z-index' }
        });
        if (onRefresh) onRefresh();
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.gapView.swal.errorTitle'),
          text: error.response?.data?.message || t('gap-analysis:assessment.gapView.swal.errorApprove'),
          customClass: { container: 'swal-high-z-index' }
        });
      }
    }
  );

  const handleSelectControl = (controlId) => {
    if (selectedControls.includes(controlId)) {
      setSelectedControls(selectedControls.filter(id => id !== controlId));
    } else {
      setSelectedControls([...selectedControls, controlId]);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const unlockableControls = controls
        .filter(c => c.response?.locked === true)
        .map(c => c._id);
      setSelectedControls(unlockableControls);
    } else {
      setSelectedControls([]);
    }
  };

  const handleUnlock = async () => {
    if (selectedControls.length === 0) {
      SwalReact.fire({
        icon: 'warning',
        title: t('gap-analysis:assessment.gapView.swal.noSelectionTitle'),
        text: t('gap-analysis:assessment.gapView.swal.noSelectionText'),
        customClass: { container: 'swal-high-z-index' }
      });
      return;
    }

    const result = await SwalReact.fire({
      title: t('gap-analysis:assessment.gapView.swal.unlockTitle'),
      html: `<p>${t('gap-analysis:assessment.gapView.swal.unlockHtml', { count: selectedControls.length })}</p>`,
      icon: 'question',
      input: 'textarea',
      inputLabel: t('gap-analysis:assessment.gapView.swal.notesLabel'),
      inputPlaceholder: t('gap-analysis:assessment.gapView.swal.notesPlaceholder'),
      showCancelButton: true,
      confirmButtonText: t('gap-analysis:assessment.gapView.swal.unlockButton'),
      customClass: { container: 'swal-high-z-index' },
    });

    if (result.isConfirmed) {
      unlockMutation.mutate({ controlIds: selectedControls, unlockNotes: result.value || '' });
    }
  };

  const handleApprove = async () => {
    const result = await SwalReact.fire({
      title: t('gap-analysis:assessment.gapView.swal.approveTitle'),
      html: `<p>${t('gap-analysis:assessment.gapView.swal.approveHtml', { score: trustScore?.percentage ?? 'N/A' })}</p>`,
      icon: 'question',
      input: 'textarea',
      inputLabel: t('gap-analysis:assessment.gapView.swal.approvalNotesLabel'),
      inputPlaceholder: t('gap-analysis:assessment.gapView.swal.approvalNotesPlaceholder'),
      showCancelButton: true,
      confirmButtonText: t('gap-analysis:assessment.gapView.swal.approveButton'),
      confirmButtonColor: '#4caf50',
      customClass: { container: 'swal-high-z-index' },
      preConfirm: (inputNotes) => {
        setNotes(inputNotes);
        return inputNotes;
      }
    });

    if (result.isConfirmed) {
      approveMutation.mutate();
    }
  };

  const handleDownloadEvidence = async (fileId) => {
    try {
      const response = await getEvidenceDownloadUrl(fileId);
      const url = response.data.data?.url || response.data.url;
      window.open(url, '_blank');
    } catch (error) {
      SwalReact.fire({
        icon: 'error',
        title: t('gap-analysis:assessment.gapView.swal.downloadFailedTitle'),
        text: error.response?.data?.message || t('gap-analysis:assessment.gapView.swal.errorDownload'),
        customClass: { container: 'swal-high-z-index' }
      });
    }
  };

  const getGapColor = (gap) => {
    if (gap === 0) return 'success';
    if (gap < 0) return 'success';
    return 'error';
  };

  const getGapLabel = (gap) => {
    if (gap === 0) return t('gap-analysis:assessment.gapView.gapLabels.meetsTarget');
    if (gap < 0) return t('gap-analysis:assessment.gapView.gapLabels.aboveTarget');
    return t('gap-analysis:assessment.gapView.gapLabels.belowTarget');
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">{t('gap-analysis:assessment.gapView.title')}</Typography>
          <Typography variant="body2" color="textSecondary">
            {t('gap-analysis:assessment.gapView.controlsSelected', { count: selectedControls.length })}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<LockOpenIcon />}
            onClick={handleUnlock}
            disabled={selectedControls.length === 0 || unlockMutation.isLoading}
          >
            {t('gap-analysis:assessment.gapView.unlockButton', { count: selectedControls.length })}
          </Button>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={approveMutation.isLoading || (trustScore && trustScore.percentage < 70)}
            >
              {t('gap-analysis:assessment.gapView.approveButton')}
            </Button>
            {trustScore && trustScore.percentage < 70 && (
              <Typography variant="caption" color="error" mt={0.5}>
                {t('gap-analysis:assessment.gapView.execRequired')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Controls Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  onChange={handleSelectAll}
                  checked={selectedControls.length > 0 && selectedControls.length === controls.filter(c => c.response?.locked === true).length}
                  indeterminate={selectedControls.length > 0 && selectedControls.length < controls.filter(c => c.response?.locked === true).length}
                />
              </TableCell>
              <TableCell>{t('gap-analysis:assessment.gapView.table.control')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.gapView.table.target')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.gapView.table.vendor')}</TableCell>
              <TableCell>{t('common:labels.status')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.gapView.table.evidence')}</TableCell>
              <TableCell>{t('gap-analysis:assessment.gapView.table.lockStatus')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {controls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">{t('gap-analysis:assessment.gapView.table.noControls')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              controls.map((control) => {
                const gap = control.targetMaturity - (control.response?.maturityLevel ?? 0);
                const isLocked = control.response?.locked === true;

                return (
                  <TableRow key={control._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedControls.includes(control._id)}
                        onChange={() => handleSelectControl(control._id)}
                        disabled={!isLocked}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{control.description}</Typography>
                      <Typography variant="caption" color="textSecondary">{control.domain}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`L${control.targetMaturity}`} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={`L${control.response?.maturityLevel ?? 0}`} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getGapLabel(gap)}
                        color={getGapColor(gap)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {control.response?.evidenceFiles && control.response.evidenceFiles.length > 0 ? (
                        control.response.evidenceFiles.map((file) => {
                          const isDownloadable = !file.scanStatus || file.scanStatus === 'clean';
                          const isMalicious = file.scanStatus === 'malicious';
                          const isPending = file.scanStatus === 'pending';

                          const tooltipTitle = isMalicious
                            ? `${file.fileName} — Threat detected${file.scanResultDetail ? ': ' + file.scanResultDetail : ''}`
                            : isPending
                              ? `${file.fileName} — Scanning...`
                              : file.fileName;

                          return (
                            <Tooltip key={file._id} title={tooltipTitle}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadEvidence(file._id, file.fileName)}
                                  disabled={!isDownloadable}
                                  sx={isMalicious ? { color: 'error.main' } : undefined}
                                >
                                  {isMalicious ? (
                                    <GppBadIcon fontSize="small" />
                                  ) : isPending ? (
                                    <GppMaybeIcon fontSize="small" />
                                  ) : (
                                    <DownloadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Typography variant="caption" color="textSecondary">{t('gap-analysis:assessment.gapView.table.noEvidence')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isLocked ? (
                        <Chip icon={<LockIcon />} label={t('gap-analysis:assessment.gapView.table.locked')} size="small" variant="outlined" />
                      ) : (
                        <Chip icon={<LockOpenIcon />} label={t('gap-analysis:assessment.gapView.table.unlocked')} size="small" color="warning" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Notes Field */}
      <Box mt={3}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('gap-analysis:assessment.gapView.internalNotes')}
          placeholder={t('gap-analysis:assessment.gapView.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Box>
    </Box>
  );
};
